import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight, ChevronLeft, LogIn, Target, Trophy, BookOpen,
  Brain, Users, Star, HelpCircle, Award, Flame, CheckCircle,
  Sparkles, MapPin, Calendar, MessageSquare, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const LOGO = '/yas-logo-alt.png';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to YAS Connect!',
    subtitle: 'Here\'s how to get started',
    description: 'YAS Connect is your digital platform for tracking growth, exploring careers, earning points, and connecting with your cohort. This quick guide shows you exactly what to do.',
    tip: 'Add YAS Connect to your phone\'s home screen for the best experience!',
    color: '#2D2824',
    emoji: '👋',
  },
  {
    id: 'login',
    title: 'Step 1: Log In',
    subtitle: 'Use the credentials from your email',
    description: 'Go to yasconnect.co on your phone or computer. Enter the email and temporary password from your welcome email.',
    steps: ['Go to yasconnect.co', 'Enter your email', 'Enter temporary password: NextGen2026!', 'Create your new password', 'Accept the agreements (one-time)'],
    color: '#E86C3D',
    emoji: '🔑',
  },
  {
    id: 'dashboard',
    title: 'Step 2: Your Dashboard',
    subtitle: 'This is your home base',
    description: 'Your dashboard shows your points, streak, worksite, and lead monitor. Scroll down to see the Win Wall, Weekly Check-in, and more.',
    highlights: [
      { label: '🏆 Points', desc: 'Everything you do earns points' },
      { label: '⚡ Quick Actions', desc: 'Interests, Skills, Courses, AI Guide' },
      { label: '🌟 Win Wall', desc: 'Post wins and celebrate others' },
      { label: '📋 This Week', desc: 'Check-in, Trivia, Skills Tracker' },
    ],
    color: '#2D2824',
    emoji: '🏠',
  },
  {
    id: 'profile',
    title: 'Step 3: Set Up Your Profile',
    subtitle: 'Make it yours — do this first!',
    description: 'Tap "Profile" at the bottom. Add your photo, school, bio, and interests. This helps the AI give you personalized career suggestions.',
    steps: ['Tap Profile in the bottom nav', 'Tap Edit Profile', 'Add your school and bio', 'Upload a photo or choose an emoji', 'Save your changes'],
    color: '#8B5CF6',
    emoji: '👤',
  },
  {
    id: 'wins',
    title: 'Step 4: Post Your First Win!',
    subtitle: 'The social hub — don\'t skip this!',
    description: 'The Wins Wall is where you celebrate each other. Post what you\'re proud of — your first day, something you learned, a goal you hit. This is the most fun part!',
    steps: ['Tap "Wins" in the bottom nav', 'Tap the + button to post', 'Write what you\'re proud of', 'Choose a category', 'Post it and earn +15 points!'],
    pointsEarned: 15,
    color: '#F59E0B',
    emoji: '🏆',
  },
  {
    id: 'goals',
    title: 'Step 5: Set Your Goals',
    subtitle: 'Set 3 goals to get paid!',
    description: 'Set SMART goals for your summer. You need at least 3 and should complete at least 2. The AI can help you write better goals.',
    steps: ['Tap Goals in the bottom nav', 'Tap "Add Summer Goal"', 'Give it a title and details', 'Set a deadline', 'Repeat — aim for at least 3!'],
    pointsEarned: '25-50 per goal',
    color: '#10B981',
    emoji: '🎯',
  },
  {
    id: 'checkin',
    title: 'Step 6: Weekly Check-in',
    subtitle: 'Do this EVERY week — 3 minutes',
    description: 'Answer 9 quick questions about your mood, highlights, and challenges. Your lead monitor reads every one — it\'s how they support you.',
    steps: ['From dashboard, tap "Weekly Check-in"', 'Share your mood', 'Share your highlight', 'Mention any challenges', 'Submit and earn +20 points'],
    pointsEarned: 20,
    color: '#3B82F6',
    emoji: '📝',
  },
  {
    id: 'ai',
    title: 'Step 7: AI Career Coach',
    subtitle: 'This will blow your mind',
    description: 'Like having a career advisor 24/7. Tell it your interests → it suggests careers with salaries, education paths, and growth outlook. It also helps with goal setting.',
    steps: ['Tap "AI Guide" from quick actions', 'Add your Interests and Skills first', 'Ask: "What careers match my interests?"', 'Try: "Help me figure out what I\'m good at"', 'Save careers you like'],
    color: '#8B5CF6',
    emoji: '🤖',
  },
  {
    id: 'courses',
    title: 'Step 8: Workshop Courses',
    subtitle: '13 courses on real-world skills',
    description: 'Interactive courses on financial literacy, leadership, communication, entrepreneurship, and more. Each earns XP.',
    highlights: [
      { label: '💰 Money Moves', desc: 'Budgeting, saving, investing' },
      { label: '👑 Boss Mode', desc: 'Leadership skills' },
      { label: '💬 Real Talk', desc: 'Communication that gets you ahead' },
      { label: '🎤 The Pitch', desc: 'Presentation skills' },
    ],
    color: '#EC4899',
    emoji: '📚',
  },
  {
    id: 'project',
    title: 'Step 9: Summer Project',
    subtitle: 'You get PAID for this!',
    description: 'Choose from 6 workbook projects. Build something real — a business plan, your personal brand, or a content channel. Easier than it sounds!',
    highlights: [
      { label: '🚀 Side Hustle Launch', desc: 'Design your own business' },
      { label: '📱 Creator Launch', desc: 'Build a TikTok/YouTube' },
      { label: '💼 Personal Brand', desc: 'Your professional identity' },
      { label: '🌍 Community Impact', desc: 'Lead a real project' },
    ],
    color: '#6366F1',
    emoji: '🔨',
  },
  {
    id: 'helpdesk',
    title: 'Step 10: Ask Questions',
    subtitle: 'Your monitor is one tap away',
    description: 'Use the Help Desk to ask your lead monitor anything. They get notified instantly and respond quickly.',
    steps: ['Tap Help Desk from dashboard', 'Type your question', 'Choose a category', 'Submit — monitor gets notified instantly', 'Check back for their response'],
    color: '#0EA5E9',
    emoji: '❓',
  },
  {
    id: 'assessment',
    title: 'Step 11: Skills Assessment',
    subtitle: '12 scenarios — +50 points!',
    description: 'Real workplace scenarios that test your judgment. You get a score AND AI feedback on every answer.',
    steps: ['From dashboard, tap "Skills Tracker"', 'Start the Situational Judgment Test', 'Read each scenario carefully', 'Choose the best response', 'Get AI feedback per question'],
    pointsEarned: 50,
    highlights: [
      { label: '💼 Professionalism', desc: 'Work readiness' },
      { label: '💬 Communication', desc: 'Express yourself' },
      { label: '🤝 Teamwork', desc: 'Working with others' },
      { label: '🧠 Problem-Solving', desc: 'Smart decisions' },
    ],
    color: '#14B8A6',
    emoji: '📊',
  },
  {
    id: 'surveys',
    title: 'Step 12: Surveys & Trivia',
    subtitle: 'Quick activities = easy points',
    description: 'Short surveys and trivia that earn points. The weekly check-in is most important — your monitor reads every response.',
    highlights: [
      { label: '📋 Post-Orientation Survey', desc: '12 questions — +15 pts' },
      { label: '📝 Weekly Check-in', desc: 'Every week — +20 pts' },
      { label: '🧩 Weekly Trivia', desc: '5 fun questions — +25 pts' },
      { label: '📈 Skills Tracker', desc: 'Rate your growth — +50 pts' },
    ],
    tip: 'The weekly check-in is the most important — your lead monitor reads every response.',
    color: '#7C3AED',
    emoji: '🎯',
  },
  {
    id: 'recap',
    title: 'You\'re Ready!',
    subtitle: 'Here\'s your full checklist',
    description: 'Complete these activities to earn points and get paid. The more you do, the more you earn. Check off each one as you go!',
    checklist: [
      { task: 'Complete your profile', pts: '—' },
      { task: 'Add your interests & skills', pts: '—' },
      { task: 'Set at least 3 SMART goals', pts: '25-50 ea' },
      { task: 'Complete 2+ goals', pts: '25-50 ea' },
      { task: 'Post on the Wins Wall', pts: '+15' },
      { task: 'Do weekly check-in (every week)', pts: '+20' },
      { task: 'Complete 1 workshop course', pts: 'XP' },
      { task: 'Start your Summer Project', pts: '—' },
      { task: 'Take the Skills Assessment (SJT)', pts: '+50' },
      { task: 'Complete Post-Orientation Survey', pts: '+15' },
      { task: 'Do Weekly Trivia (every week)', pts: '+25' },
      { task: 'Try the AI Career Coach', pts: '+5' },
    ],
    color: '#10B981',
  }
];

const UserFlowGuide = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const step = STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}CC 100%)` }} className="p-6 pb-8 transition-all duration-500">
        <div className="max-w-2xl mx-auto text-center">
          {isFirst && <img src={LOGO} alt="" className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white p-2" />}
          {!isFirst && step.emoji && <span className="text-5xl block mb-3">{step.emoji}</span>}
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {isFirst ? 'Getting Started Guide' : `Step ${currentStep} of ${STEPS.length - 2}`}
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold mb-2" style={{ color: 'white' }}>{step.title}</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>{step.subtitle}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-2xl mx-auto px-6 -mt-2">
        <div className="flex gap-1">
          {STEPS.map((_, i) => (
            <button key={i} onClick={() => setCurrentStep(i)} className={`flex-1 h-1.5 rounded-full transition-all ${i === currentStep ? 'bg-orange-500' : i < currentStep ? 'bg-green-500' : 'bg-gray-300'}`} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div key={currentStep} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
            
            <Card className="p-6 rounded-2xl bg-white shadow-sm mb-4">
              <p className="text-gray-700 leading-relaxed mb-4">{step.description}</p>

              {/* Numbered Steps */}
              {step.steps && (
                <div className="space-y-2 mb-4">
                  {step.steps.map((s, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded-xl bg-gray-50">
                      <span className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: step.color }}>{i + 1}</span>
                      <span className="text-sm text-gray-700">{s}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Highlights */}
              {step.highlights && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {step.highlights.map((h, i) => (
                    <div key={i} className="p-3 rounded-xl bg-gray-50">
                      <p className="font-bold text-xs text-gray-900">{h.label}</p>
                      <p className="text-[11px] text-gray-500">{h.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Checklist */}
              {step.checklist && (
                <div className="space-y-1.5">
                  {step.checklist.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-md border-2 border-gray-300 flex items-center justify-center text-[10px]" />
                        <span className="text-sm text-gray-700">{c.task}</span>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700 text-[10px]">{c.pts}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Points earned */}
              {step.pointsEarned && (
                <div className="mt-4 p-3 rounded-xl bg-orange-50 border border-orange-200 flex items-center gap-3">
                  <Star className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <p className="text-sm font-semibold text-orange-800">Points earned: +{step.pointsEarned}</p>
                </div>
              )}

              {/* Tip */}
              {step.tip && (
                <div className="mt-4 p-3 rounded-xl bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-700"><strong>Pro tip:</strong> {step.tip}</p>
                </div>
              )}
            </Card>

            {/* Navigation */}
            <div className="flex gap-3 mb-6">
              {!isFirst && (
                <Button onClick={() => setCurrentStep(c => c - 1)} variant="outline" className="flex-1 rounded-xl gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back
                </Button>
              )}
              {isLast ? (
                <Button onClick={() => navigate('/login')} className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl gap-1 text-white">
                  <LogIn className="w-4 h-4" /> Log In Now
                </Button>
              ) : (
                <Button onClick={() => setCurrentStep(c => c + 1)} className="flex-1 rounded-xl gap-1" style={{ backgroundColor: step.color }}>
                  {isFirst ? 'Get Started' : 'Next Step'} <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Quick jump */}
            {!isFirst && !isLast && (
              <p className="text-center text-xs text-gray-400">
                <button onClick={() => setCurrentStep(STEPS.length - 1)} className="underline hover:text-gray-600">Skip to checklist</button>
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="text-center pb-8 px-6">
        <p className="text-xs text-gray-400">Newark SYEP NextGEN · Powered by YAS Connect</p>
      </div>
    </div>
  );
};

export default UserFlowGuide;
